/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useEffect } from 'react';
// Store
import {
	TParams,
	TQueryParams,
	TSelectOption,
} from '@global-types/index';
import useStore from '@store/index';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TAdditionalityProjectQueryParams } from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */

export const useAdditionalityProject = () => {
	const {
		setAdditionalityProjectsQueryParams,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
		setAdditionalityProjectsPageSize,
		setAdditionalityProjectsPage,
		addAdditionalityProjectFormOpened,
		setAddAdditionalityProjectFormOpened,
	} = useStore();

	/**************************** Pagination ****************************/
	const handleActiveFilter = (status: TQueryParams[]) => {
		setAdditionalityProjectsPage(1);
		const queryParamsActive = {
			active: status.map(
				(_status: TParams) => _status.value
			),
		};

		setAdditionalityProjectsQueryParams({
			...additionalityProjectsQueryParams,
			...queryParamsActive,
		});
	};

	const handleChangePaginationSize = function (
		_size: string | number
	) {
		setAdditionalityProjectsPageSize(Number(_size));
		setAdditionalityProjectsPage(1);
	};

	const handleChangePagination = (
		page: string | number
	) => {
		setAdditionalityProjectsPage(page);
	};

	/**
	 * Filter list by NAME
	 *
	 * @param {TSelectOption} _NAMES
	 */
	const handleProjectNameFilter = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		setAdditionalityProjectsPage(1);
		const queryParamsProjects = {
			name: event.target.value,
		};

		const filterParams: TAdditionalityProjectQueryParams =
			{
				...additionalityProjectsQueryParams,
				...queryParamsProjects,
			};
		setAdditionalityProjectsQueryParams(filterParams);
	};

	/**
	 * Filter list by technology
	 *
	 * @param {TSelectOption} _technologies
	 */
	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setAdditionalityProjectsPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TAdditionalityProjectQueryParams =
			removeEmptyArrays({
				...additionalityProjectsQueryParams,
				...queryParamsProjects,
			});
		setAdditionalityProjectsQueryParams(filterParams);
	};

	/**
	 * Filter list by country
	 *
	 * @param {TSelectOption} _countries
	 */
	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setAdditionalityProjectsPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(additionalityProjectsQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setAdditionalityProjectsQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TAdditionalityProjectQueryParams =
			removeEmptyArrays({
				...additionalityProjectsQueryParams,
				...queryParamsCountries,
			});
		setAdditionalityProjectsQueryParams(filterParams);
	};

	const handleOpenDrawerAddAdditionalityProject = () => {
		setAddAdditionalityProjectFormOpened(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setAdditionalityProjectsQueryParams({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/********************************************************************************************/
	return {
		handleProjectNameFilter,
		handleCountryFilter,
		handleTechnologyFilter,
		handleChangePaginationSize,
		handleChangePagination,
		additionalityProjectsPage,
		additionalityProjectsPageSize,
		additionalityProjectsQueryParams,
		setAdditionalityProjectsPageSize,
		setAdditionalityProjectsPage,
		addAdditionalityProjectFormOpened,
		setAddAdditionalityProjectFormOpened,
		handleOpenDrawerAddAdditionalityProject,
		handleActiveFilter,
		setAdditionalityProjectsQueryParams,
	};
};
